import React, { useState, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from "@auth0/auth0-react";
import axios from 'axios';
import { useNotification } from '../../context/NotificationContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';

import CellTemplateMaker from '../../components/canvas/CellTemplateMaker.jsx'
import RectangleShape from '../../components/shapes/Rectangle/RectangleShape.jsx';
import CircleShape from '../../components/shapes/Circle/CircleShape.jsx';
import ArcShape from '../../components/shapes/Arc/ArcShape.jsx';
import ArrowShape from '../../components/shapes/Arrow/ArrowShape.jsx';
import ImageShape from '../../components/shapes/Image/ImageShape.jsx';
import TextShape from '../../components/shapes/Text/TextShape.jsx';
import LineShape from '../../components/shapes/Line/LineShape.jsx';
import TagShape from '../../components/shapes/Tag/TagShape.jsx';
import StarShape from '../../components/shapes/Star/StarShape.jsx';
import RingShape from '../../components/shapes/Ring/RingShape.jsx';
import RectangleIcon from '../../components/shapes/icons/RectangleIcon.js';
import CircleIcon from '../../components/shapes/icons/CircleIcon.js';
import ArcIcon from '../../components/shapes/icons/ArcIcon.js';
import ImageIcon from '../../components/shapes/icons/ImageIcon.js';
import TextIcon from '../../components/shapes/icons/TextIcon.js';
import StarIcon from '../../components/shapes/icons/StarIcon.js';
import RingIcon from '../../components/shapes/icons/RingIcon.js';
import LineIcon from '../../components/shapes/icons/LineIcon.js';
import { useSelectedShape } from '../../hooks/useSelectedShape';
import { useAppState } from '../../hooks/useAppState';
import './AdTemplateManager.css';

// Import shape property components
import RectangleProperties from '../../components/shapes/properties/RectangleProperties';
import CircleProperties from '../../components/shapes/properties/CircleProperties';
import ArcProperties from '../../components/shapes/properties/ArcProperties';
import StarProperties from '../../components/shapes/properties/StarProperties';
import RingProperties from '../../components/shapes/properties/RingProperties';
import ImageProperties from '../../components/shapes/properties/ImageProperties';
import TextProperties from '../../components/shapes/properties/TextProperties';
import LineProperties from '../../components/shapes/properties/LineProperties';

// Visual layout patterns for product arrangements
const LAYOUT_PATTERNS = {
  1: [
    { id: 'full', label: 'Full cell', preview: [{ x: 2, y: 2, w: 36, h: 36 }] },
    { id: 'centered', label: 'Centered', preview: [{ x: 8, y: 8, w: 24, h: 24 }] },
  ],
  2: [
    { id: 'side-by-side', label: 'Side by side', preview: [{ x: 1, y: 2, w: 18, h: 36 }, { x: 21, y: 2, w: 18, h: 36 }] },
    { id: 'overlap-right', label: 'Overlap right', preview: [{ x: 1, y: 4, w: 22, h: 32 }, { x: 16, y: 4, w: 22, h: 32 }] },
    { id: 'overlap-stagger', label: 'Staggered overlap', preview: [{ x: 1, y: 8, w: 22, h: 30 }, { x: 14, y: 2, w: 22, h: 30 }] },
    { id: 'stacked', label: 'Stacked', preview: [{ x: 2, y: 1, w: 36, h: 18 }, { x: 2, y: 21, w: 36, h: 18 }] },
    { id: 'big-left', label: 'Big + small', preview: [{ x: 1, y: 2, w: 24, h: 36 }, { x: 27, y: 10, w: 12, h: 20 }] },
    { id: 'front-back', label: 'Front & back', preview: [{ x: 12, y: 2, w: 24, h: 28 }, { x: 4, y: 8, w: 24, h: 30 }] },
  ],
  3: [
    { id: 'row', label: 'Row of 3', preview: [{ x: 1, y: 6, w: 11, h: 28 }, { x: 14, y: 6, w: 11, h: 28 }, { x: 27, y: 6, w: 11, h: 28 }] },
    { id: 'cluster', label: 'Cluster', preview: [{ x: 2, y: 6, w: 18, h: 28 }, { x: 10, y: 2, w: 18, h: 28 }, { x: 20, y: 8, w: 18, h: 28 }] },
    { id: 'pyramid', label: 'Pyramid', preview: [{ x: 12, y: 1, w: 16, h: 18 }, { x: 2, y: 20, w: 16, h: 18 }, { x: 22, y: 20, w: 16, h: 18 }] },
    { id: 'fan', label: 'Fan', preview: [{ x: 1, y: 6, w: 18, h: 30 }, { x: 10, y: 3, w: 18, h: 30 }, { x: 19, y: 6, w: 18, h: 30 }] },
    { id: '1-big-2-small', label: '1 big + 2 small', preview: [{ x: 1, y: 2, w: 24, h: 36 }, { x: 27, y: 2, w: 12, h: 17 }, { x: 27, y: 21, w: 12, h: 17 }] },
    { id: 'top-row', label: 'Top 1 + bottom 2', preview: [{ x: 6, y: 1, w: 28, h: 18 }, { x: 1, y: 21, w: 18, h: 18 }, { x: 21, y: 21, w: 18, h: 18 }] },
  ],
  4: [
    { id: 'grid-2x2', label: '2\u00d72 Grid', preview: [{ x: 1, y: 1, w: 18, h: 18 }, { x: 21, y: 1, w: 18, h: 18 }, { x: 1, y: 21, w: 18, h: 18 }, { x: 21, y: 21, w: 18, h: 18 }] },
    { id: 'fan-4', label: 'Fan of 4', preview: [{ x: 1, y: 6, w: 14, h: 28 }, { x: 9, y: 4, w: 14, h: 28 }, { x: 17, y: 4, w: 14, h: 28 }, { x: 25, y: 6, w: 14, h: 28 }] },
    { id: '1-big-3-small', label: '1 big + 3 small', preview: [{ x: 1, y: 2, w: 24, h: 36 }, { x: 27, y: 1, w: 12, h: 11 }, { x: 27, y: 14, w: 12, h: 11 }, { x: 27, y: 27, w: 12, h: 11 }] },
    { id: 'row-4', label: 'Row of 4', preview: [{ x: 1, y: 8, w: 8, h: 24 }, { x: 11, y: 8, w: 8, h: 24 }, { x: 21, y: 8, w: 8, h: 24 }, { x: 31, y: 8, w: 8, h: 24 }] },
  ],
};

function AdTemplateManager() {
  const { getAccessTokenSilently } = useAuth0();
  const { organizationId } = useAppState();
  const notification = useNotification();

  const [templateWidth, setTemplateWidth] = useState(400);
  const [templateHeight, setTemplateHeight] = useState(400);
  const [templateData, setTemplateData] = useState([[[], null], [[], null]]);
  const [currentTemplateIndex, setCurrentTemplateIndex] = useState(0);
  const [backgroundColor, setBackgroundColor] = useState('white');
  const [templates, setTemplates] = useState([]);
  const [shapesLayerRef, setShapesLayerRef] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editingTemplateId, setEditingTemplateId] = useState(null);
  const [adDimensions, setAdDimensions] = useState('1x1');
  const [zoom, setZoom] = useState(1);
  const [showCenterGuides, setShowCenterGuides] = useState(false);
  const [clipboardShape, setClipboardShape] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // --- Enhanced template mode (always on) ---
  const designMode = 'enhanced';
  const [activeLayer, setActiveLayer] = useState('static'); // 'static' | 'shared' | 'product'
  const [staticShapes, setStaticShapes] = useState([]);
  const [sharedShapes, setSharedShapes] = useState([]);
  const [productBlockShapes, setProductBlockShapes] = useState([]);
  const [productBlockConfig, setProductBlockConfig] = useState({
    layout: 'grid',
    gap: 8,
    min_products: 1,
    max_products: 1,
  });
  const [showProductSettings, setShowProductSettings] = useState(false);
  const productSettingsRef = React.useRef(null);
  const [previewProductCount, setPreviewProductCount] = useState(1);
  // Cell visual properties
  const [cellBackgroundColor, setCellBackgroundColor] = useState('');
  const [cellBorderColor, setCellBorderColor] = useState('');
  const [cellBorderWidth, setCellBorderWidth] = useState(0);
  const [cellBorderRadius, setCellBorderRadius] = useState(0);
  const [cellPadding, setCellPadding] = useState(0);

  // Use the useSelectedShape hook instead of separate state variables
  const {
    shapes,
    setShapes,
    selectedId,
    setSelectedId,
    selectedShape,
    handleShapeChange,
    addShape,
    removeShape,
    moveShapeUp,
    moveShapeDown,
    clearSelection
  } = useSelectedShape([]);

  const location = useLocation();
  const navigate = useNavigate();

  const defaultPoints = [(templateWidth/2), (templateHeight/2), (templateWidth/4), (templateHeight/4)];

  const handleAdDimensionsChange = (event) => {
    setAdDimensions(event.target.value);

    switch(event.target.value) {
      case '1x1':
        setTemplateHeight(400);
        setTemplateWidth(400);
        break;
      case '1x1&1/2':
        setTemplateHeight(400);
        setTemplateWidth(600);
        break;
      case '1x2':
        setTemplateHeight(400);
        setTemplateWidth(800);
        break;
      case '1x3':
        setTemplateHeight(400);
        setTemplateWidth(1200);
        break;
      case '1/2x3':
        setTemplateHeight(200);
        setTemplateWidth(1200);
        break;
      case '2x1':
        setTemplateHeight(800);
        setTemplateWidth(400);
        break;
      case '3x1':
        setTemplateHeight(1200);
        setTemplateWidth(400);
        break;
      case '4x1':
        setTemplateHeight(1600);
        setTemplateWidth(400);
        break;
      case '1&1/2x1':
        setTemplateHeight(600);
        setTemplateWidth(400);
        break;
      case '1x3/4':
        setTemplateHeight(400);
        setTemplateWidth(300);
        break;
      case '1x3/2':
        setTemplateHeight(400);
        setTemplateWidth(600);
        break;
      case '2x1&1/2':
        setTemplateHeight(800);
        setTemplateWidth(600);
        break;
      default:
        break;
    }

    // Reset all shape-related state
    setShapes([]);
    setSelectedId(null);
    setTemplateData([[[], null], [[], null]]);
    setCurrentTemplateIndex(0);
    setBackgroundColor('white');
    setShapesLayerRef(null);
    setEditing(false);
    setEditingTemplateId(null);
  };

  // Close product settings popover on outside click
  useEffect(() => {
    if (!showProductSettings) return;
    const handleClickOutside = (e) => {
      if (productSettingsRef.current && !productSettingsRef.current.contains(e.target)) {
        setShowProductSettings(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showProductSettings]);

  useEffect(() => {
    if (location.state && location.state.adTemplate) {
      const loadAdTemplate = async (templateId) => {
        // Fetch the template data
        const token = await getAccessTokenSilently();
        const response = await axios.get(`${process.env.REACT_APP_SERVER_DOMAIN}/api/templates/${templateId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const templateData = JSON.parse(response.data.data);
        // Update local state
        setAdDimensions(response.data.ad_dimensions);

        switch(response.data.ad_dimensions) {
          case '1x1':
            setTemplateHeight(400);
            setTemplateWidth(400);
            break;
          case '1x1&1/2':
            setTemplateHeight(400);
            setTemplateWidth(600);
            break;
          case '1x2':
            setTemplateHeight(400);
            setTemplateWidth(800);
            break;
          case '1x3':
            setTemplateHeight(400);
            setTemplateWidth(1200);
            break;
          case '1/2x3':
            setTemplateHeight(200);
            setTemplateWidth(1200);
            break;
          case '2x1':
            setTemplateHeight(800);
            setTemplateWidth(400);
            break;
          case '3x1':
            setTemplateHeight(1200);
            setTemplateWidth(400);
            break;
          case '4x1':
            setTemplateHeight(1600);
            setTemplateWidth(400);
            break;
          case '1&1/2x1':
            setTemplateHeight(600);
            setTemplateWidth(400);
            break;
          case '1x3/4':
            setTemplateHeight(400);
            setTemplateWidth(300);
            break;
          case '1x3/2':
            setTemplateHeight(400);
            setTemplateWidth(600);
            break;
          case '2x1&1/2':
            setTemplateHeight(800);
            setTemplateWidth(600);
            break;
          default:
            break;
        }

        setShapes(templateData);
        setEditing(true);
        setEditingTemplateId(templateId);

        // Load enhanced template fields if present
        if (response.data.static_shapes || response.data.product_block) {
          setStaticShapes(response.data.static_shapes ? JSON.parse(response.data.static_shapes) : []);
          setSharedShapes(response.data.shared_shapes ? JSON.parse(response.data.shared_shapes) : []);
          const pb = response.data.product_block;
          if (pb) {
            setProductBlockShapes(pb.shapes ? JSON.parse(pb.shapes) : []);
            setProductBlockConfig({
              layout: pb.layout || 'grid',
              gap: pb.gap ?? 8,
              min_products: pb.min_products ?? 1,
              max_products: pb.max_products ?? 6,
              layouts: pb.layouts || {},
            });
          }
          // Start on static layer with its shapes loaded
          setActiveLayer('static');
          setShapes(response.data.static_shapes ? JSON.parse(response.data.static_shapes) : []);
        }

        // Load cell visual properties
        if (response.data.background_color) setCellBackgroundColor(response.data.background_color);
        if (response.data.border_color) setCellBorderColor(response.data.border_color);
        if (response.data.border_width) setCellBorderWidth(response.data.border_width);
        if (response.data.border_radius) setCellBorderRadius(response.data.border_radius);
        if (response.data.cell_padding) setCellPadding(response.data.cell_padding);
      };
      loadAdTemplate(location.state.adTemplate._id || location.state.adTemplate.id);
    }
  }, [location, getAccessTokenSilently, setShapes]);

  const onShapesLayerReady = (shapesLayer) => {
    setShapesLayerRef(shapesLayer);
  }

  // Render a placeholder preview for variable text
  const renderVariablePlaceholder = (text) => {
    if (!text) return '';

    // Replace variable placeholders with a styled version for better visibility
    let displayText = text;
    const variablePattern = /\{\{([^}]+)\}\}/g;

    // Use a sample date format to provide a realistic preview
    const sampleData = {
      startDate: 'Mar 14, 2025',
      endDate: 'Mar 16, 2025',
      flyerTitle: 'Weekly Specials',
      effectiveDates: 'Mar 14 to 16, 2025'
    };

    // Replace all variables with their sample values
    displayText = displayText.replace(variablePattern, (match, varName) => {
      const sampleValue = sampleData[varName] || `[${varName}]`;
      return sampleValue;
    });

    return displayText;
  };

  const fetchTemplates = async () => {
    if (!organizationId) return;

    try {
      const token = await getAccessTokenSilently();
      // Use axios to fetch templates
      const response = await axios.get(`${process.env.REACT_APP_SERVER_DOMAIN}/api/templates/organization/${organizationId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      // Set the templates state
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
    }
  };

  const handleTemplatePreviewImageUpload = (file) => {
    return new Promise(async (resolve, reject) => {
      const token = await getAccessTokenSilently();

      if (!file) {
        resolve(null);
        return;
      }

      const response = await axios.get(`${process.env.REACT_APP_SERVER_DOMAIN}/api/s3/signed-url?type=template&fileName=${encodeURIComponent(file.name)}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const { data: { signedUrl } } = response;
      if (signedUrl) {
        const result = await axios.put(signedUrl, file, {
          headers: {
            'Content-Type': file.type
          }
        });

        if (result.status === 200) {
          const imageUrl = signedUrl.split("?")[0];
          resolve(imageUrl); // Resolve the promise with imageUrl
        } else {
          console.error('Error uploading file:', result);
          reject('Error uploading file.');
        }
      } else {
        console.error('Server did not return a signed URL for upload.');
        reject('Server did not return a signed URL for upload.');
      }
    });
  };

  const DeleteTemplate = async (templateId) => {
    const token = await getAccessTokenSilently();
    try {
      const response = await axios.delete(`${process.env.REACT_APP_SERVER_DOMAIN}/api/templates/${templateId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if(response.status === 200) {
        // Check if the template being deleted is the same one being edited
        notification.success('Template was successfully deleted.');

        if (editing && templateId === editingTemplateId) {
          setEditing(false);
          setEditingTemplateId(null);
          handleCancel();
        }
        // Fetch the templates again after the operation is done
        fetchTemplates();
      }
    } catch (error) {
      notification.error('Error occurred while deleting the template.');
    }
  }

  const handleDelete = (event) => {
    event.preventDefault();
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    setDeleteDialogOpen(false);
    DeleteTemplate(editingTemplateId);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleSave = async(event) => {
    event.preventDefault();
    saveTemplate(event);
  }

  const handleSaveAndClose = async(event) => {
    event.preventDefault();
    saveTemplate(event).then(() => {
      handleCancel();
    }).catch(error => {
      // Handle error
      console.error('An error occurred:', error);
    });
  }

  const handleCancel = () => {
     navigate('/adtemplates');
  };

  const saveTemplate = async () => {
    clearSelection();

    const token = await getAccessTokenSilently();

    // Create a temporary canvas
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = templateWidth;
    tempCanvas.height = templateHeight;
    const ctx = tempCanvas.getContext('2d');

    // Draw the background rectangle (use cell background color if set)
    ctx.fillStyle = cellBackgroundColor || backgroundColor;
    ctx.fillRect(0, 0, templateWidth, templateHeight);

    // For enhanced mode, generate a composite preview with all 3 layers
    // For legacy mode, just use the single shapesLayerRef as before
    let previewDataUrl;
    if (shapesLayerRef) {
      const Konva = (await import('konva')).default;
      // Resolve final shapes for each layer (active layer uses current `shapes`)
      let finalStatic = activeLayer === 'static' ? deepCopy(shapes) : deepCopy(staticShapes);
      let finalShared = activeLayer === 'shared' ? deepCopy(shapes) : deepCopy(sharedShapes);
      let finalProduct = activeLayer === 'product' ? deepCopy(shapes) : deepCopy(productBlockShapes);
      const allShapes = [...finalStatic, ...finalShared, ...finalProduct];

      // Pre-load all images before rendering
      const imageShapes = allShapes.filter(s => s.type === 'image' && s.imageUrl);
      const loadedImages = {};
      await Promise.all(imageShapes.map(s => new Promise((res) => {
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => { loadedImages[s.id] = img; res(); };
        img.onerror = () => res(); // skip failed images
        img.src = s.imageUrl;
      })));

      // Clone the shapes into the existing Konva layer temporarily
      const layer = shapesLayerRef;
      const originalChildren = layer.getChildren().slice();
      layer.removeChildren();

      // Add all layer shapes as simple Konva nodes
      const tempNodes = [];
      for (const s of allShapes) {
        let node;
        if (s.type === 'rectangle') {
          node = new Konva.Rect({ x: s.x, y: s.y, width: s.width, height: s.height, fill: s.fill, stroke: s.stroke, strokeWidth: s.strokeWidth, cornerRadius: s.cornerRadius, rotation: s.rotation, opacity: s.opacity });
        } else if (s.type === 'circle') {
          node = new Konva.Circle({ x: s.x, y: s.y, radius: s.radius, fill: s.fill, stroke: s.stroke, strokeWidth: s.strokeWidth, rotation: s.rotation, opacity: s.opacity });
        } else if (s.type === 'text') {
          node = new Konva.Text({ x: s.x, y: s.y, text: s.text, fontSize: s.fontSize, fontFamily: s.fontFamily, fill: s.fill, width: s.width, align: s.align, fontStyle: s.fontStyle, textDecoration: s.textDecoration, rotation: s.rotation, opacity: s.opacity });
        } else if (s.type === 'image') {
          const loadedImg = loadedImages[s.id];
          if (loadedImg) {
            node = new Konva.Image({ x: s.x, y: s.y, width: s.width, height: s.height, image: loadedImg, cornerRadius: s.cornerRadius, rotation: s.rotation, opacity: s.opacity, stroke: s.stroke, strokeWidth: s.strokeWidth });
          } else {
            continue;
          }
        } else if (s.type === 'line') {
          node = new Konva.Line({ points: s.points, stroke: s.stroke, strokeWidth: s.strokeWidth, rotation: s.rotation, opacity: s.opacity });
        } else if (s.type === 'arrow') {
          node = new Konva.Arrow({ points: s.points, stroke: s.stroke, strokeWidth: s.strokeWidth, fill: s.fill, pointerLength: s.pointerLength, pointerWidth: s.pointerWidth, rotation: s.rotation, opacity: s.opacity });
        } else if (s.type === 'star') {
          node = new Konva.Star({ x: s.x, y: s.y, numPoints: s.numPoints, innerRadius: s.innerRadius, outerRadius: s.outerRadius, fill: s.fill, stroke: s.stroke, strokeWidth: s.strokeWidth, rotation: s.rotation, opacity: s.opacity });
        } else if (s.type === 'arc') {
          node = new Konva.Arc({ x: s.x, y: s.y, innerRadius: s.innerRadius, outerRadius: s.outerRadius, angle: s.angle, fill: s.fill, stroke: s.stroke, strokeWidth: s.strokeWidth, rotation: s.rotation, opacity: s.opacity });
        } else if (s.type === 'ring') {
          node = new Konva.Ring({ x: s.x, y: s.y, innerRadius: s.innerRadius, outerRadius: s.outerRadius, fill: s.fill, stroke: s.stroke, strokeWidth: s.strokeWidth, rotation: s.rotation, opacity: s.opacity });
        } else if (s.type === 'tag') {
          node = new Konva.Label({ x: s.x, y: s.y, rotation: s.rotation, opacity: s.opacity });
          node.add(new Konva.Tag({ fill: s.fill, stroke: s.stroke, strokeWidth: s.strokeWidth, pointerDirection: s.pointerDirection, pointerWidth: s.pointerWidth, pointerHeight: s.pointerHeight, cornerRadius: s.cornerRadius }));
          node.add(new Konva.Text({ text: s.text, fontSize: s.fontSize, fontFamily: s.fontFamily, fill: s.fontColor || '#000', padding: s.padding }));
        } else {
          continue;
        }
        if (node) {
          layer.add(node);
          tempNodes.push(node);
        }
      }
      layer.draw();
      previewDataUrl = layer.toDataURL();

      // Restore original children
      tempNodes.forEach(n => n.destroy());
      layer.removeChildren();
      originalChildren.forEach(c => layer.add(c));
      layer.draw();
    } else {
      previewDataUrl = shapesLayerRef.toDataURL();
    }

    // Draw the shapes onto the canvas
    const shapesImage = new Image();
    return new Promise((resolve, reject) => {

      shapesImage.onload = async () => {
        ctx.drawImage(shapesImage, 0, 0, templateWidth, templateHeight);

        // Generate the data URL from the canvas
        const dataUrl = tempCanvas.toDataURL();
        // Creating blob from the base64
        const response1 = await fetch(dataUrl);
        const blob = await response1.blob();
        // Convert the blob into a file
        let previewImageFile = new File([blob], "preview.png", {
          type: "image/png",
        });

        // Upload image and get the URL
        const uploadedImageUrl = await handleTemplatePreviewImageUpload(previewImageFile);

        const shapesData = JSON.stringify(shapes);

        try {
          const url = editing ?
          `${process.env.REACT_APP_SERVER_DOMAIN}/api/templates/${editingTemplateId}` :
          `${process.env.REACT_APP_SERVER_DOMAIN}/api/templates/`;

          const method = editing ? 'patch' : 'post';

          // Build request body
          const requestBody = {
            data: shapesData,
            ad_dimensions: adDimensions,
            preview_image_url: uploadedImageUrl,
            organization_id: organizationId,
          };

          // Save all layer shapes
          // Save current layer before persisting
          let finalStatic = staticShapes;
          let finalShared = sharedShapes;
          let finalProduct = productBlockShapes;
          if (activeLayer === 'static') finalStatic = deepCopy(shapes);
          else if (activeLayer === 'shared') finalShared = deepCopy(shapes);
          else if (activeLayer === 'product') finalProduct = deepCopy(shapes);

          requestBody.static_shapes = JSON.stringify(finalStatic);
          requestBody.shared_shapes = JSON.stringify(finalShared);
          requestBody.product_block = {
            shapes: JSON.stringify(finalProduct),
            block_width: templateWidth,
            block_height: templateHeight,
            ...productBlockConfig,
          };

          // Auto-detect if this template can display products
          // (has linkedProductProperty in shared or product layers)
          const allLayerShapes = [...finalShared, ...finalProduct];
          requestBody.is_product_cell = allLayerShapes.some(s => s.linkedProductProperty);

          // Add cell visual properties
          if (cellBackgroundColor) requestBody.background_color = cellBackgroundColor;
          if (cellBorderColor) requestBody.border_color = cellBorderColor;
          if (cellBorderWidth) requestBody.border_width = cellBorderWidth;
          if (cellBorderRadius) requestBody.border_radius = cellBorderRadius;
          if (cellPadding) requestBody.cell_padding = cellPadding;

          // Post the dataUrl to the server
          const response2 = await axios[method](url, requestBody, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });

          if(response2.status === 200){
            if(editing) {
              notification.success('Template was successfully updated.');

            } else if(!editing) {
              notification.success('Template was successfully saved.');
              setEditing(true);
              setEditingTemplateId(response2.data.result._id);
            }
            fetchTemplates();
            resolve();
          }
        } catch (error) {
          notification.error('Error occurred while saving the template.');
          reject();
        }
      };
      shapesImage.onerror = () => {
        console.error("Error loading shapesImage");
        // Reject the Promise if there is an error
        reject(new Error('Error loading shapesImage'));
      };
      shapesImage.src = previewDataUrl;
    });
  };

  function deepCopy(obj) {
    if (typeof obj !== 'object' || obj === null) {
      // Return if obj is not an object or is null
      return obj;
    }

    let copy = Array.isArray(obj) ? [] : {};

    for (let key in obj) {
      copy[key] = deepCopy(obj[key]);
    }

    return copy;
  }

  useEffect(() => {
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessTokenSilently, organizationId]);

  // Suppress unused warning - templates is used for future features
  void templates;

  useEffect(() => {
    const newTemplates = [...templateData];
    newTemplates[currentTemplateIndex] = [deepCopy(shapes), selectedId];
    setTemplateData(newTemplates);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapes, selectedId]);

  // Create a separate effect for switching templates
  useEffect(() => {
    // Don't do anything if templateData array is empty
    if (templateData[0].length === 0 && templateData[1].length === 0) return;

    const [newShapes, newSelectedId] = templateData[currentTemplateIndex];
    setShapes(deepCopy(newShapes));
    setSelectedId(newSelectedId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTemplateIndex]);

  // Copy/Paste keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check for Cmd+C (Mac) or Ctrl+C (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'c') {
        if (selectedShape) {
          // Copy the selected shape to clipboard
          setClipboardShape(deepCopy(selectedShape));
        }
      }

      // Check for Cmd+V (Mac) or Ctrl+V (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'v') {
        if (clipboardShape) {
          e.preventDefault(); // Prevent default paste behavior

          // Create a new shape with a unique ID and offset position
          const newShape = deepCopy(clipboardShape);
          const timestamp = Date.now();
          newShape.id = `${newShape.type}${shapes.length + 1}_${timestamp}`;

          // Offset the position slightly (20px right and down)
          if (newShape.x !== undefined) {
            newShape.x = newShape.x + 20;
          }
          if (newShape.y !== undefined) {
            newShape.y = newShape.y + 20;
          }
          // For line shapes, offset all points
          if (newShape.points) {
            newShape.points = newShape.points.map((point, index) => {
              // Even indices are x, odd indices are y
              return point + 20;
            });
          }

          // Add the new shape (it will be added at the end, which is the top layer)
          addShape(newShape);
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedShape, clipboardShape, shapes.length, addShape]);

  // --- Layer switching for enhanced mode ---
  const saveCurrentLayerShapes = () => {
    // Always in enhanced mode
    const current = deepCopy(shapes);
    if (activeLayer === 'static') setStaticShapes(current);
    else if (activeLayer === 'shared') setSharedShapes(current);
    else if (activeLayer === 'product') setProductBlockShapes(current);
  };

  const switchLayer = (newLayer) => {
    if (newLayer === activeLayer) return;
    saveCurrentLayerShapes();
    clearSelection();
    setActiveLayer(newLayer);
    if (newLayer === 'static') setShapes(deepCopy(staticShapes));
    else if (newLayer === 'shared') setShapes(deepCopy(sharedShapes));
    else if (newLayer === 'product') setShapes(deepCopy(productBlockShapes));
  };

  const handleStageClick = () => {
    clearSelection();
  };

  const handleAddRectangle = () => {
    const id = `rectangle${shapes.length + 1}_${Date.now()}`;
    const rectangle = {
        type: 'rectangle',
        id,
        x: (templateWidth/2),
        y: (templateHeight/2),
        width: 50,
        height: 50,
        fill: "white",
        stroke: null,
        strokeWidth: null,
        cornerRadius: null,
        rotation: 0,
        draggable: true
    };

    addShape(rectangle);
  };

  const handleAddCircle = () => {
    const id = `circle${shapes.length + 1}_${Date.now()}`;
    const circle = {
        type: 'circle',
        id,
        x: (templateWidth/2),
        y: (templateHeight/2),
        radius: 50,
        fill: "white",
        stroke: null,
        strokeWidth: null,
        draggable: true
    };

    addShape(circle);
  };

  const handleAddArc = () => {
    const id = `arc${shapes.length + 1}_${Date.now()}`;
    const arc = {
      type: 'arc',
      id,
      x: (templateWidth/2),
      y: (templateHeight/2),
      innerRadius: 40,
      outerRadius: 60,
      angle: 60,
      fill: "white",
      stroke: null,
      strokeWidth: null,
      cornerRadius: null,
      draggable: true
    };
    addShape(arc);
  };


  const handleAddImage = () => {
    const id = `image${shapes.length + 1}_${Date.now()}`;
    const image = {
        type: 'image',
        id,
        x: (templateWidth/2),
        y: (templateHeight/2),
        width: 100,
        height: 100,
        imageUrl: "/product-image-placeholder.webp",
        align: 'center',
        vAlign: 'middle',
        stroke: null,
        strokeWidth: null,
        cornerRadius: null,
        linkedProductNumber: null,
        linkedProductProperty: null,
        draggable: true
      };
    addShape(image);
  };

  const handleAddText = () => {
    const id = `text${shapes.length + 1}_${Date.now()}`;
    const text = {
        type: 'text',
        id,
        x: (templateWidth/2),
        y: (templateHeight/2),
        width: 240,
        height: 16,
        text: "Some Text Here",
        fontFamily: "Arial",
        fontSize: 16,
        fontStyle: "normal",
        textDecoration: "none",
        align: "left",
        verticalAlign: "top",
        padding: 0,
        lineHeight: 1,
        wrap: "word",
        ellipsis: false,
        fill: "#000000",
        linkedProductNumber: null,
        linkedProductProperty: null,
        draggable: true,
        shadowColor: "black",
        shadowBlur: 0,
        shadowOffsetX: 0,
        shadowOffsetY: 0,
        hasVariables: false
      };

    addShape(text);
  };

  const handleAddLine = () => {
    const id = `line${shapes.length + 1}_${Date.now()}`;
    const line = {
      type: 'line',
      id,
      points: JSON.parse(JSON.stringify(defaultPoints)),
      stroke: "black",
      strokeWidth: 4,
      draggable: true,
      rotation: 0
    };

    addShape(line);
  };


  const handleAddStar = () => {
    const id = `star${shapes.length + 1}_${Date.now()}`;
    const star = {
      type: 'star',
      id,
      x: (templateWidth/2),
      y: (templateHeight/2),
      numPoints: 5,
      innerRadius: 20,
      outerRadius: 40,
      fill: 'yellow',
      stroke: null,
      strokeWidth: null,
      cornerRadius: null,
      draggable: true
    };

    addShape(star);
  };

  const handleAddRing = () => {
    const id = `ring${shapes.length + 1}_${Date.now()}`;
    const ring = {
        type: 'ring',
        id,
        x: (templateWidth/2),
        y: (templateHeight/2),
        innerRadius: 20,
        outerRadius: 40,
        fill: 'blue',
        stroke: null,
        strokeWidth: null,
        draggable: true
    };

    addShape(ring);
  };

  // Get shape-specific properties component
  const getShapeProperties = () => {
    if (!selectedShape) return null;

    switch(selectedShape.type) {
      case 'rectangle':
        return <RectangleProperties shape={selectedShape} onChange={handleShapeChange} />;
      case 'circle':
        return <CircleProperties shape={selectedShape} onChange={handleShapeChange} />;
      case 'arc':
        return <ArcProperties shape={selectedShape} onChange={handleShapeChange} />;
      case 'image':
        return <ImageProperties shape={selectedShape} onChange={handleShapeChange} />;
      case 'star':
        return <StarProperties shape={selectedShape} onChange={handleShapeChange} />;
      case 'ring':
        return <RingProperties shape={selectedShape} onChange={handleShapeChange} />;
      case 'line':
        return <LineProperties shape={selectedShape} onChange={handleShapeChange} />;
      case 'text':
        return <TextProperties shape={selectedShape} onChange={handleShapeChange} />;
      default:
        return null;
    }
  };

  // --- Ghost shapes: show lower layers as non-interactive reference ---
  const ghostLayerShapes = useMemo(() => {
    // Always in enhanced mode
    // Layer 0 = static, Layer 1 = shared, Layer 2 = product
    // When on layer 0: no ghosts
    // When on layer 1: show static (layer 0)
    // When on layer 2: show static (layer 0) + shared (layer 1)
    if (activeLayer === 'static') return [];
    if (activeLayer === 'shared') return deepCopy(staticShapes);
    if (activeLayer === 'product') return [...deepCopy(staticShapes), ...deepCopy(sharedShapes)];
    return [];
  }, [activeLayer, staticShapes, sharedShapes]);

  const renderGhostShape = (shape) => {
    const ghostProps = { key: 'ghost-' + shape.id, id: 'ghost-' + shape.id, draggable: false, listening: false };
    switch (shape.type) {
      case 'rectangle':
        return <RectangleShape {...shape} {...ghostProps} selected={false} onSelect={() => {}} onChange={() => {}} />;
      case 'circle':
        return <CircleShape {...shape} {...ghostProps} selected={false} onSelect={() => {}} onChange={() => {}} />;
      case 'arc':
        return <ArcShape {...shape} {...ghostProps} selected={false} onSelect={() => {}} onChange={() => {}} />;
      case 'arrow':
        return <ArrowShape {...shape} {...ghostProps} selected={false} onSelect={() => {}} onChange={() => {}} />;
      case 'image':
        return <ImageShape {...shape} {...ghostProps} selected={false} onClick={() => {}} onChange={() => {}} />;
      case 'text':
        return <TextShape {...shape} {...ghostProps} selected={false} onSelect={() => {}} onChange={() => {}} />;
      case 'line':
        return <LineShape {...shape} {...ghostProps} selected={false} onSelect={() => {}} onChange={() => {}} />;
      case 'tag':
        return <TagShape {...shape} {...ghostProps} selected={false} onSelect={() => {}} onChange={() => {}} />;
      case 'star':
        return <StarShape {...shape} {...ghostProps} selected={false} onSelect={() => {}} onChange={() => {}} />;
      case 'ring':
        return <RingShape {...shape} {...ghostProps} selected={false} onSelect={() => {}} onChange={() => {}} />;
      default:
        return null;
    }
  };

  return (
    <div className="ad-template-editor">

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete this template?"
        description="This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Main Toolbar */}
      <div className="editor-toolbar">
        {/* Ad Size Selector */}
        <div className="toolbar-section">
          <label className="toolbar-label">Ad Size</label>
          <select
            value={adDimensions || '1x1'}
            onChange={handleAdDimensionsChange}
            className="toolbar-select"
          >
            <option value="1x1">1x1</option>
            <option value="1x3/4">1x3/4</option>
            <option value="1x3/2">1x3/2</option>
            <option value="1x2">1x2</option>
            <option value="1x3">1x3</option>
            <option value="1/2x1">1/2x1</option>
            <option value="1/2x2">1/2x2</option>
            <option value="1/2x3">1/2x3</option>
            <option value="2x1">2x1</option>
            <option value="3x1">3x1</option>
            <option value="4x1">4x1</option>
            <option value="1x1&1/2">1&1/2x1</option>
            <option value="2x1&1/2">2x1&1/2</option>
          </select>
        </div>

        <div className="toolbar-divider" />

        {/* Shape Tools */}
        <div className="toolbar-section shapes-toolbar">
          {/* In enhanced mode, filter shapes per layer:
              - shared (Price & Info): text only
              - product (Product Images): image only
              - static (Background): all shapes */}
          {(activeLayer === 'static') && (
            <>
              <button onClick={handleAddRectangle} className="shape-tool-btn" title="Add Rectangle">
                <RectangleIcon width={20} height={15} color="#ff8264" />
              </button>
              <button onClick={handleAddCircle} className="shape-tool-btn" title="Add Circle">
                <CircleIcon size={20} color="#ff8264" />
              </button>
              <button onClick={handleAddArc} className="shape-tool-btn" title="Add Arc">
                <ArcIcon size={20} color="#ff8264" />
              </button>
            </>
          )}
          {(activeLayer === 'static' || activeLayer === 'product') && (
            <button onClick={handleAddImage} className="shape-tool-btn" title="Add Image">
              <ImageIcon size={20} color="#ff8264" />
            </button>
          )}
          {(activeLayer === 'static' || activeLayer === 'shared') && (
            <button onClick={handleAddText} className="shape-tool-btn" title="Add Text">
              <TextIcon size={20} color="#ff8264" />
            </button>
          )}
          {(activeLayer === 'static') && (
            <>
              <button onClick={handleAddStar} className="shape-tool-btn" title="Add Star">
                <StarIcon size={20} color="#ff8264" />
              </button>
              <button onClick={handleAddRing} className="shape-tool-btn" title="Add Ring">
                <RingIcon size={20} color="#ff8264" />
              </button>
              <button onClick={handleAddLine} className="shape-tool-btn" title="Add Line">
                <LineIcon size={20} color="#ff8264" />
              </button>
            </>
          )}
        </div>

        {/* Element Controls - visible when shape is selected */}
        {selectedShape && (
          <>
            <div className="toolbar-divider" />
            <div className="toolbar-section element-controls">
              <button
                onClick={() => moveShapeUp(selectedShape.id)}
                className="control-btn"
                title="Move Up (Layer)"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 19V5M5 12l7-7 7 7" />
                </svg>
                <span className="btn-label">Move Up</span>
              </button>
              <button
                onClick={() => moveShapeDown(selectedShape.id)}
                className="control-btn"
                title="Move Down (Layer)"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 5v14M5 12l7 7 7-7" />
                </svg>
                <span className="btn-label">Move Down</span>
              </button>
              <button
                onClick={() => removeShape(selectedShape.id)}
                className="control-btn delete-btn"
                title="Delete Shape"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                </svg>
                <span className="btn-label">Delete</span>
              </button>
            </div>
          </>
        )}

        {/* Spacer to push zoom controls to the right */}
        <div className="toolbar-spacer" />

        {/* Zoom Controls */}
        <div className="toolbar-section zoom-controls">
          <button
            className="zoom-btn"
            onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
            disabled={zoom <= 0.5}
            title="Zoom Out"
          >
            −
          </button>
          <input
            type="range"
            className="zoom-slider"
            min="0.5"
            max="3"
            step="0.1"
            value={zoom}
            onChange={(e) => setZoom(parseFloat(e.target.value))}
          />
          <span className="zoom-level">{Math.round(zoom * 100)}%</span>
          <button
            className="zoom-btn"
            onClick={() => setZoom(Math.min(3, zoom + 0.1))}
            disabled={zoom >= 3}
            title="Zoom In"
          >
            +
          </button>
        </div>

        {/* Canvas Guide Controls */}
        <div className="toolbar-section guide-controls">
          <button
            className={`guide-btn ${showCenterGuides ? 'active' : ''}`}
            onClick={() => setShowCenterGuides(!showCenterGuides)}
            title="Toggle Center Guidelines"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="3 2">
              <line x1="12" y1="2" x2="12" y2="22" />
              <line x1="2" y1="12" x2="22" y2="12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Properties Panel - visible when shape is selected */}
      {selectedShape && (
        <div className="properties-panel">
          <div className="properties-content">
            {getShapeProperties()}
          </div>
        </div>
      )}

      {/* Editor Body: Layers Panel + Main Canvas Area */}
      <div className="editor-body">
          <div className="layers-panel">
            <div className="layers-header">Layers</div>
            <div className="layers-list">
              {/* Layer 2 — Product Layout (top) */}
              <div
                className={`layer-item ${activeLayer === 'product' ? 'active' : ''}`}
                onClick={() => switchLayer('product')}
                ref={productSettingsRef}
              >
                <span className="layer-num">2</span>
                <span className="layer-name">Product Images</span>
                <span className="layer-count">{activeLayer === 'product' ? shapes.length : productBlockShapes.length}</span>
                {(activeLayer === 'product' ? shapes : productBlockShapes).some(s => s.type === 'image') && (
                  <button
                    className="product-settings-trigger"
                    onClick={(e) => { e.stopPropagation(); setShowProductSettings(v => !v); }}
                    title="Product layout settings"
                  >
                    <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M11.078 0l.855 3.424a7.593 7.593 0 012.064 1.196l3.378-1.015 1.078 1.867-2.525 2.408c.106.39.17.8.17 1.225 0 .406-.06.798-.158 1.174l2.528 2.412-1.078 1.867-3.396-1.02a7.57 7.57 0 01-2.035 1.17L11.078 18H8.922l-.856-3.424a7.593 7.593 0 01-2.064-1.196L2.624 14.4l-1.078-1.867 2.525-2.408A5.932 5.932 0 013.9 8.9c0-.406.06-.798.158-1.174L1.53 5.314l1.078-1.867 3.396 1.02A7.57 7.57 0 018.04 3.297L8.922 0h2.156zM10 6a3 3 0 100 6 3 3 0 000-6z"/>
                    </svg>
                  </button>
                )}
                {showProductSettings && (
                  <div className="product-settings-popover">
                    <div className="ps-row">
                      <label>Max Products</label>
                      <select
                        value={productBlockConfig.max_products}
                        onChange={(e) => setProductBlockConfig(c => ({ ...c, max_products: parseInt(e.target.value) || 1 }))}
                      >
                        {[1, 2, 3, 4].map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                    {productBlockConfig.max_products > 1 && (
                      <div className="ps-row">
                        <label>Gap (px)</label>
                        <input type="number" value={productBlockConfig.gap} min={0} max={40}
                          onChange={(e) => setProductBlockConfig(c => ({ ...c, gap: parseInt(e.target.value) || 0 }))} />
                      </div>
                    )}
                    {productBlockConfig.max_products > 1 && (
                      <div className="ps-layouts">
                        <label className="ps-layouts-label">Arrangement</label>
                        {[2, 3, 4].filter(n => n <= productBlockConfig.max_products).map(count => {
                          const patterns = LAYOUT_PATTERNS[count] || [];
                          const selectedPattern = (productBlockConfig.layouts || {})[count] || patterns[0]?.id;
                          return (
                            <div key={count} className="ps-layout-row">
                              <span className="ps-count-label">{count}</span>
                              <div className="ps-pattern-options">
                                {patterns.map(pattern => (
                                  <button
                                    key={pattern.id}
                                    className={`pattern-btn ${selectedPattern === pattern.id ? 'active' : ''}`}
                                    onClick={() => setProductBlockConfig(c => ({
                                      ...c,
                                      layouts: { ...(c.layouts || {}), [count]: pattern.id }
                                    }))}
                                    title={pattern.label}
                                  >
                                    <svg viewBox="0 0 40 40" width="32" height="32">
                                      {pattern.preview.map((rect, ri) => (
                                        <rect key={ri} x={rect.x} y={rect.y} width={rect.w} height={rect.h} rx="2"
                                          fill={selectedPattern === pattern.id ? '#ff8264' : '#cbd5e1'} />
                                      ))}
                                    </svg>
                                  </button>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Layer 1 — Price & Info */}
              <div
                className={`layer-item ${activeLayer === 'shared' ? 'active' : ''}`}
                onClick={() => switchLayer('shared')}
              >
                <span className="layer-num">1</span>
                <span className="layer-name">Price & Info</span>
                <span className="layer-count">{activeLayer === 'shared' ? shapes.length : sharedShapes.length}</span>
              </div>
              {/* Layer 0 — Background (bottom) */}
              <div
                className={`layer-item ${activeLayer === 'static' ? 'active' : ''}`}
                onClick={() => switchLayer('static')}
              >
                <span className="layer-num">0</span>
                <span className="layer-name">Background</span>
                <span className="layer-count">{activeLayer === 'static' ? shapes.length : staticShapes.length}</span>
              </div>
            </div>
            <div className="layer-description">
              {activeLayer === 'static' && 'Backgrounds and decorations behind everything.'}
              {activeLayer === 'shared' && 'Text elements for price or promo info shown once per cell.'}
              {activeLayer === 'product' && 'Product images that repeat for each product in the cell.'}
            </div>
          </div>

        {/* Mobile bottom tabs */}
          <div className="layers-bottom-tabs">
            <button className={`lbt ${activeLayer === 'static' ? 'active' : ''}`} onClick={() => switchLayer('static')}>
              <span className="lbt-num">0</span> Background
            </button>
            <button className={`lbt ${activeLayer === 'shared' ? 'active' : ''}`} onClick={() => switchLayer('shared')}>
              <span className="lbt-num">1</span> Price & Info
            </button>
            <button className={`lbt ${activeLayer === 'product' ? 'active' : ''}`} onClick={() => switchLayer('product')}>
              <span className="lbt-num">2</span> Product Images
              {(activeLayer === 'product' ? shapes : productBlockShapes).some(s => s.type === 'image') && (
                <span
                  className="lbt-gear"
                  onClick={(e) => { e.stopPropagation(); setShowProductSettings(v => !v); }}
                >
                  <svg width="12" height="12" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M11.078 0l.855 3.424a7.593 7.593 0 012.064 1.196l3.378-1.015 1.078 1.867-2.525 2.408c.106.39.17.8.17 1.225 0 .406-.06.798-.158 1.174l2.528 2.412-1.078 1.867-3.396-1.02a7.57 7.57 0 01-2.035 1.17L11.078 18H8.922l-.856-3.424a7.593 7.593 0 01-2.064-1.196L2.624 14.4l-1.078-1.867 2.525-2.408A5.932 5.932 0 013.9 8.9c0-.406.06-.798.158-1.174L1.53 5.314l1.078-1.867 3.396 1.02A7.57 7.57 0 018.04 3.297L8.922 0h2.156zM10 6a3 3 0 100 6 3 3 0 000-6z"/>
                  </svg>
                </span>
              )}
            </button>
          </div>

        <div className="editor-main">
      {/* Cell Properties Panel */}
      <div className="cell-properties-bar">
        <div className="cell-prop-group">
          <label>Background</label>
          <input type="color" value={cellBackgroundColor || '#ffffff'}
            onChange={(e) => setCellBackgroundColor(e.target.value)} />
          {cellBackgroundColor && (
            <button className="cell-prop-clear" onClick={() => setCellBackgroundColor('')} title="Clear">x</button>
          )}
        </div>
        <div className="cell-prop-group">
          <label>Border</label>
          <input type="color" value={cellBorderColor || '#e5e7eb'}
            onChange={(e) => setCellBorderColor(e.target.value)} />
          <input type="number" value={cellBorderWidth} min={0} max={10} style={{ width: 50 }}
            onChange={(e) => setCellBorderWidth(parseInt(e.target.value) || 0)} placeholder="Width" />
        </div>
        <div className="cell-prop-group">
          <label>Radius</label>
          <input type="number" value={cellBorderRadius} min={0} max={40} style={{ width: 50 }}
            onChange={(e) => setCellBorderRadius(parseInt(e.target.value) || 0)} />
        </div>
        <div className="cell-prop-group">
          <label>Padding</label>
          <input type="number" value={cellPadding} min={0} max={40} style={{ width: 50 }}
            onChange={(e) => setCellPadding(parseInt(e.target.value) || 0)} />
        </div>
      </div>

      {/* Canvas Area */}
      <div className="canvas-container">
        <div className="canvas-wrapper">
          <div className="canvas-stage">
            <CellTemplateMaker
              width={templateWidth}
              height={templateHeight}
              scale={zoom}
              onStageClick={handleStageClick}
              backgroundColor={cellBackgroundColor || backgroundColor}
              onBackgroundColorChange={setBackgroundColor}
              saveTemplate={saveTemplate}
              onShapesLayerReady={onShapesLayerReady}
              showCenterGuides={showCenterGuides}
              cellBorderColor={cellBorderColor}
              cellBorderWidth={cellBorderWidth}
              cellBorderRadius={cellBorderRadius}
              cellPadding={cellPadding}
              ghostChildren={ghostLayerShapes.length > 0 ? ghostLayerShapes.map(renderGhostShape) : null}
            >
          {shapes.map((shape) => {
            switch(shape.type) {
              case 'rectangle':
                return (
                  <RectangleShape
                    key={shape.id}
                    id={shape.id}
                    x={shape.x}
                    y={shape.y}
                    width={shape.width}
                    height={shape.height}
                    fill={shape.fill}
                    draggable={shape.draggable}
                    stroke={shape.stroke}
                    strokeWidth={shape.strokeWidth}
                    cornerRadius={shape.cornerRadius}
                    rotation={shape.rotation}
                    selected={selectedId === shape.id}
                    onSelect={() => setSelectedId(shape.id)}
                    onChange={handleShapeChange}
                  />
                );
              case 'circle':
                return (
                  <CircleShape
                    key={shape.id}
                    id={shape.id}
                    x={shape.x}
                    y={shape.y}
                    radius={shape.radius}
                    fill={shape.fill}
                    draggable={shape.draggable}
                    stroke={shape.stroke}
                    strokeWidth={shape.strokeWidth}
                    selected={selectedId === shape.id}
                    onSelect={() => setSelectedId(shape.id)}
                    onChange={handleShapeChange}
                  />
                );
              case 'arc':
                return (
                  <ArcShape
                    key={shape.id}
                    id={shape.id}
                    x={shape.x}
                    y={shape.y}
                    innerRadius={shape.innerRadius}
                    outerRadius={shape.outerRadius}
                    angle={shape.angle}
                    fill={shape.fill}
                    draggable={shape.draggable}
                    stroke={shape.stroke}
                    strokeWidth={shape.strokeWidth}
                    cornerRadius={shape.cornerRadius}
                    selected={selectedId === shape.id}
                    onSelect={() => setSelectedId(shape.id)}
                    onChange={handleShapeChange}
                  />
                );
              case 'arrow':
                return (
                  <ArrowShape
                    key={shape.id}
                    id={shape.id}
                    points={shape.points}
                    pointerLength={shape.pointerLength}
                    pointerWidth={shape.pointerWidth}
                    fill={shape.fill}
                    draggable={shape.draggable}
                    stroke={shape.stroke}
                    selected={selectedId === shape.id}
                    onSelect={() => setSelectedId(shape.id)}
                    onChange={handleShapeChange}
                  />
                );
              case 'image':
                return (
                  <ImageShape
                    key={shape.id}
                    id={shape.id}
                    x={shape.x}
                    y={shape.y}
                    width={shape.width}
                    height={shape.height}
                    imageUrl={shape.imageUrl}
                    align={shape.align}
                    vAlign={shape.vAlign}
                    draggable={shape.draggable}
                    stroke={shape.stroke}
                    strokeWidth={shape.strokeWidth}
                    cornerRadius={shape.cornerRadius}
                    linkedProductNumber={shape.linkedProductNumber}
                    linkedProductProperty={shape.linkedProductProperty}
                    selected={selectedId === shape.id}
                    onClick={() => {
                      setSelectedId(shape.id);
                    }}
                    onChange={handleShapeChange}
                  />
                );
              case 'text':
                return (
                  <TextShape
                    key={shape.id}
                    id={shape.id}
                    x={shape.x}
                    y={shape.y}
                    width={shape.width}
                    height={shape.height}
                    text={shape.text}
                    draggable={shape.draggable}
                    fontFamily={shape.fontFamily}
                    fontSize={shape.fontSize}
                    fontStyle={shape.fontStyle}
                    textDecoration={shape.textDecoration}
                    align={shape.align}
                    verticalAlign={shape.verticalAlign}
                    padding={shape.padding}
                    lineHeight={shape.lineHeight}
                    wrap={shape.wrap}
                    ellipsis={shape.ellipsis}
                    fill={shape.fill}
                    linkedProductNumber={shape.linkedProductNumber}
                    linkedProductProperty={shape.linkedProductProperty}
                    selected={selectedId === shape.id}
                    onSelect={() => setSelectedId(shape.id)}
                    onChange={handleShapeChange}
                    shadowColor={shape.shadowColor}
                    shadowBlur={shape.shadowBlur}
                    shadowOffsetX={shape.shadowOffsetX}
                    shadowOffsetY={shape.shadowOffsetY}
                    hasVariables={shape.hasVariables}
                    renderVariablePlaceholder={renderVariablePlaceholder}
                  />
                );
              case 'line':
                return (
                  <LineShape
                    key={shape.id}
                    id={shape.id}
                    points={shape.points}
                    stroke={shape.stroke}
                    draggable={shape.draggable}
                    strokeWidth={shape.strokeWidth}
                    rotation={shape.rotation}
                    selected={selectedId === shape.id}
                    onSelect={() => setSelectedId(shape.id)}
                    onChange={handleShapeChange}
                  />
                );
              case 'tag':
                return (
                  <TagShape
                    key={shape.id}
                    id={shape.id}
                    x={shape.x}
                    y={shape.y}
                    text={shape.text}
                    fill={shape.fill}
                    draggable={shape.draggable}
                    stroke={shape.stroke}
                    strokeWidth={shape.strokeWidth}
                    pointerDirection={shape.pointerDirection}
                    pointerWidth={shape.pointerWidth}
                    pointerHeight={shape.pointerHeight}
                    selected={selectedId === shape.id}
                    onSelect={() => setSelectedId(shape.id)}
                    onChange={(newAttrs) => {
                      const newAttrsDeepClone = JSON.parse(JSON.stringify(newAttrs));
                      handleShapeChange(newAttrsDeepClone);
                    }}
                  />
                );
              case 'star':
                return (
                  <StarShape
                    key={shape.id}
                    id={shape.id}
                    x={shape.x}
                    y={shape.y}
                    numPoints={shape.numPoints}
                    innerRadius={shape.innerRadius}
                    outerRadius={shape.outerRadius}
                    fill={shape.fill}
                    draggable={shape.draggable}
                    selected={shape.id === selectedId}
                    stroke={shape.stroke}
                    strokeWidth={shape.strokeWidth}
                    cornerRadius={shape.cornerRadius}
                    onSelect={() => setSelectedId(shape.id)}
                    onChange={handleShapeChange}
                  />
                );
              case 'ring':
                return (
                  <RingShape
                    key={shape.id}
                    id={shape.id}
                    x={shape.x}
                    y={shape.y}
                    innerRadius={shape.innerRadius}
                    outerRadius={shape.outerRadius}
                    fill={shape.fill}
                    draggable={shape.draggable}
                    selected={shape.id === selectedId}
                    stroke={shape.stroke}
                    strokeWidth={shape.strokeWidth}
                    cornerRadius={shape.cornerRadius}
                    onSelect={() => setSelectedId(shape.id)}
                    onChange={handleShapeChange}
                  />
                );
              default:
                return null;
            }
          })}
            </CellTemplateMaker>
          </div>
        </div>
      </div>
        </div>{/* end editor-main */}
      </div>{/* end editor-body */}

      {/* Bottom Action Bar */}
      <div className="action-bar">
        <button className="action-btn secondary" onClick={handleCancel}>
          Cancel
        </button>
        <button className="action-btn primary" onClick={handleSave}>
          {editing ? 'Update' : 'Save'}
        </button>
        <button className="action-btn primary-dark" onClick={handleSaveAndClose}>
          {editing ? 'Update & Close' : 'Save & Close'}
        </button>
        {editing && (
          <button className="action-btn danger" onClick={handleDelete}>
            Delete
          </button>
        )}
      </div>
    </div>
  );
}

export default AdTemplateManager;
